import { browser } from '$app/environment';
import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
import type { RaceResponse, UserEventDataResponse } from '$types/races';
import { selectedRaceId } from '$utils/navigation';
import { derived, get } from 'svelte/store';

async function fetchRace(
  f: typeof fetch,
  auth: { accessToken: string | undefined; guestId: string | undefined },
  variables: {
    id: string;
    bookmakers?: string[];
    bookmakersWithFlucs?: string[];
    hasBetfairBackFlucs?: boolean;
    hasBetfairLayFlucs?: boolean;
  },
  { retriesLeft = 5, retryDelay = 1000 } = {}
) {
  const headers = {
    'Content-Type': 'application/json'
  } as Record<string, string>;
  if (auth.accessToken) {
    headers['Authorization'] = `Bearer ${auth.accessToken}`;
  } else if (auth.guestId) {
    headers['X-Guest-Id'] = auth.guestId;
  } else {
    throw new Error('No auth provided');
  }
  if (!variables.id) {
    throw new Error('id must be provided');
  }

  // add gzip, deflate, br to accept-encoding header
  // headers['Accept-Encoding'] = 'gzip, deflate, br';

  const raceData = f(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query:
        `
        query Race($id: ID!, $bookmakers: [String!], $bookmakersWithFlucs: [String!]) {
            race(id: $id) {
                id
                betfairMapping {
                    win
                    place
                }
                meeting {
                    id
                    track
                    location
                    type
                    date
                }
                name
                number
                status
                startTime
                actualStartTime
                distance
                results
                links {
                    bookmaker
                    navLink
                    affiliateLink
                    lastSuccessfulPriceUpdate
                }
                runners {
                    id
                    number
                    barrier
                    name
                    trainerName
                    riderName
                    scratchedTime
                    betfairMarkets {
                        id
                        sp
                        marketName
                        totalMatched
                        marketTotalMatched
                        lastPriceTraded
                        ` +
        (variables.hasBetfairBackFlucs
          ? ` backFlucs {
                            price
                            lastUpdated
                        } `
          : ' ') +
        `
                        ` +
        (variables.hasBetfairLayFlucs
          ? ` layFlucs {
                            price
                            lastUpdated
                        } `
          : ' ') +
        `
                        back {
                            price
                            size
                            lastUpdated
                        }
                        lay {
                            price
                            size
                            lastUpdated
                        }
                    }
                    bookmakerMarkets(bookmakers: $bookmakers, bookmakersWithFlucs: $bookmakersWithFlucs) {
                        id
                        bookmaker
                        fixedWin {
                            flucs {
                                price
                                lastUpdated
                            }
                            openingPrice
                            price
                            lastUpdated
                        }
                        fixedPlace {
                            flucs {
                                price
                                lastUpdated
                            }
                            openingPrice
                            price
                            lastUpdated
                        }
                    }
                }
            }
        }
      `,
      variables
    })
  })
    .then((res: Response) => res.json())
    .catch((err) => {
      console.error(err);
      if (retriesLeft > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(
              fetchRace(f, auth, variables, {
                retriesLeft: retriesLeft - 1,
                retryDelay: retryDelay * 2
              })
            );
          }, retryDelay);
        });
      } else {
        throw err;
      }
    }) as Promise<RaceResponse>;

  return raceData;
}

async function fetchUserEventData(
  f: typeof fetch,
  auth: { accessToken: string | undefined },
  variables: {
    input: { eventId: string };
  },
  { retriesLeft = 5, retryDelay = 1000 } = {}
) {
  const headers = {
    'Content-Type': 'application/json'
  } as Record<string, string>;
  if (auth.accessToken) {
    headers['Authorization'] = `Bearer ${auth.accessToken}`;
  } else {
    throw new Error('No auth provided');
  }
  if (!variables.input.eventId) {
    throw new Error('eventId must be provided');
  }

  const userEventData = f(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: `
        query UserEventData($input: UserEventDataInput!) {
            userEventData(input: $input) {
              eventId
              userId
              data {
                columnName
                selectionData {
                  selectionId
                  value
                }
              }
            }
          }
        `,
      variables
    })
  })
    .then((res: Response) => res.json())
    .catch((err) => {
      console.error(err);
      if (retriesLeft > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(
              fetchUserEventData(f, auth, variables, {
                retriesLeft: retriesLeft - 1,
                retryDelay
              })
            );
          }, retryDelay);
        });
      } else {
        throw err;
      }
    }) as Promise<UserEventDataResponse>;

  return userEventData;
}

export const load = async ({ depends, params, parent, fetch, setHeaders }) => {
  depends('betwatch:race');
  const { races, session, guestId, layoutConfigStore } = await parent();

  const { date, type, track, number } = params;

  const races_ = get(races);

  if (!races_?.data) {
    return { session };
  }

  const race = races_.data?.races.find((r) => {
    return (
      r.meeting.date === date &&
      r.meeting.type[0] === type &&
      r.meeting.track === track &&
      r.number.toString() === number
    );
  });

  if (!race) return { session, race };

  // create a derived store that will update when the selectedRaceId changes
  const raceStore = derived([races], ([$races]) => {
    if (!$races) return undefined;
    const race = $races.data?.races.find((r) => {
      return (
        r.meeting.date === date &&
        r.meeting.type[0] === type &&
        r.meeting.track === track &&
        r.number.toString() === number
      );
    });
    return race;
  });

  // only request the required bookmakers and flucs
  let bookmakersWithFlucs: string[] = [];
  let bookmakers: string[] = [];

  let hasBetfairBackFlucs = false;
  let hasBetfairLayFlucs = false;

  if (browser) {
    const activeConfig = get(layoutConfigStore.activeLayoutConfig);

    if (activeConfig) {
      const layoutsWithFlucs = activeConfig.layoutPreferences.filter((p) => {
        return (
          p.bookmaker &&
          (p.type === 'FIXED_PLACE_FLUCS' || p.type === 'FIXED_WIN_FLUCS')
        );
      });

      if (
        layoutsWithFlucs.some(
          (p) => p.bookmaker === 'Betfair' && p.side === 'BACK'
        )
      ) {
        hasBetfairBackFlucs = true;
      }
      if (
        layoutsWithFlucs.some(
          (p) => p.bookmaker === 'Betfair' && p.side === 'LAY'
        )
      ) {
        hasBetfairLayFlucs = true;
      }

      bookmakersWithFlucs = layoutsWithFlucs.map((p) => p.bookmaker as string);

      // check if there is no "show remaining" bookmakers
      if (
        activeConfig.showRemainingBookmakers === 'FIXED_WIN_FLUCS' ||
        activeConfig.showRemainingBookmakers === 'FIXED_PLACE_FLUCS'
      ) {
        // empty array will return all bookmakers
        bookmakersWithFlucs = [];
      } else if (
        activeConfig.showRemainingBookmakers === 'NONE' ||
        activeConfig.showRemainingBookmakers === 'SPACER' ||
        activeConfig.showRemainingBookmakers === 'TEXT'
      ) {
        // populate "bookmakers with only" bookmakers
        bookmakers = activeConfig.layoutPreferences
          .filter((p) => {
            return (
              p.bookmaker &&
              (p.type === 'FIXED_WIN' || p.type === 'FIXED_PLACE') &&
              p.bookmaker !== 'Betfair'
            );
          })
          .map((p) => p.bookmaker as string);
      }
    }
  }

  //   if both are empty, then we want to show all bookmakers, but don't get the flucs
  //   if (browser && bookmakersWithFlucs.length === 0 && bookmakers.length === 0) {
  //     bookmakersWithFlucs = [''];
  //   }

  const raceData = await fetchRace(
    fetch,
    { accessToken: session?.access_token, guestId },
    {
      id: race.id,
      bookmakersWithFlucs,
      bookmakers,
      hasBetfairBackFlucs,
      hasBetfairLayFlucs
    }
  );

  let userEventData: UserEventDataResponse | undefined;
  if (session) {
    userEventData = await fetchUserEventData(
      fetch,
      { accessToken: session.access_token },
      { input: { eventId: race.id } }
    );
  }

  const title = `R${race.number} ${
    race.meeting?.track
  } - ${race.startTime?.toDateString()} - Betwatch Racing Odds Comparison`;

  return {
    title,
    fetch,
    session,
    race: raceStore,
    bookmakersWithFlucs,
    streamed: {
      raceData,
      userEventData
    }
  };
};
